import {
    Component,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy,
    inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subject, filter, takeUntil, tap } from 'rxjs';
import { PageHeaderNavComponent } from '../../../../../../shared/components/page-header-nav/page-header-nav.component';
import { NavigationText } from '../../../../../../core/constants/navigation-text';
import { NavigationOptions } from '../../../../../../shared/components/page-header-nav/models/navigation-options';
import { RouteConstants } from '../../../../../../core/constants/routes';
import { PageHeaderNavItem } from '../../../../../../shared/components/page-header-nav/models/page-header-nav-item';
import { ApplicationState } from '../../../../../../core/models/application-state/application-state';
import { StepNavigationService } from '../../../../shared/services/step-navigation/step-navigation.service';
import { StepNavigationButtonService } from '../../../../shared/services/step-navigation-button/step-navigation-button.service';
import { ApplicationStateService } from '../../../../shared/services/application-state/application-state.service';
import { ApplicationFormControlManagerService } from '../../../../shared/services/application-form-control-manager/application-form-control-manager.service';
import { DirectEntryStepPaths } from '../../../../direct-entry/shared/constants/direct-entry-steps';
import { StepLink } from '../../../../shared/models/step-link';
import { StepNavigationButtonVisibilityService } from '../../../../shared/services/step-navigation-button-visibility/step-navigation-button-visibility.service';
import { ReturnRouteService } from '../../../../../../core/services/navigation/return-route.service';

@Component({
    selector: 'app-frn-filing-action-bar',
    templateUrl: './frn-filing-action-bar.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [PageHeaderNavComponent],
})
export class FrnFilingActionBarComponent implements OnInit, OnDestroy {
    navItems: NavigationOptions = {};
    applicationState: ApplicationState | null = null;
    applicationId: string | undefined;

    public title: string | undefined;

    private destroy$ = new Subject<void>();

    private readonly router = inject(Router);
    private readonly stepNavigationService = inject(StepNavigationService);
    private readonly stepNavigationButtonService = inject(
        StepNavigationButtonService,
    );
    private readonly stepNavigationOptionsService = inject(
        StepNavigationButtonVisibilityService,
    );
    private readonly applicationStateService = inject(ApplicationStateService);
    private readonly applicationFormControlManagerService = inject(
        ApplicationFormControlManagerService,
    );
    private readonly returnRouteService = inject(ReturnRouteService);

    constructor() {}

    ngOnInit(): void {
        this.subscribeToStepChanges();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    previous() {
        this.stepNavigationService.triggerPreviousClick();
    }

    next() {
        this.stepNavigationService.triggerNextClick();
    }

    saveAndClose() {
        this.stepNavigationService.triggerSaveAndCloseClick();
    }

    handoverToEwa() {
        this.stepNavigationService.triggerHandoverToEwaClick();
    }

    cancel() {
        if (
            this.applicationState?.hasFrnRequest &&
            this.applicationId &&
            RouteConstants.FrnFiling.isExistingFrnFilingRoute(this.router.url)
        ) {
            // Navigate back to Applicant Info page
            this.router.navigate([
                RouteConstants.Application.getDirectEntryFullRouteWithApplicationId(
                    DirectEntryStepPaths.applicantInfo,
                    this.applicationId,
                ),
            ]);
        } else {
            const returnRoute = this.returnRouteService.consume();
            if (returnRoute) {
                this.router.navigate([returnRoute]);
            } else {
                this.router.navigate([
                    RouteConstants.Dashboard.getFullRoute('INDEX'),
                ]);
            }
        }
    }

    private subscribeToStepChanges(): void {
        this.stepNavigationService.currentStep$
            .pipe(
                takeUntil(this.destroy$),
                filter(
                    (currentStep): currentStep is StepLink =>
                        currentStep !== undefined,
                ),
                tap((currentStep: StepLink) => {
                    this.applicationState =
                        this.applicationStateService.getCurrentApplicationState();
                    this.title = currentStep.title;
                    this.applicationId =
                        this.applicationState?.applicationId.toString();
                    this.setNavigationLinks(currentStep);
                }),
            )
            .subscribe();
    }

    private setNavigationLinks(stepLink: StepLink) {
        this.setPrimaryLinks(stepLink);
        this.setSecondaryLinks(stepLink);
        this.setTertiaryLinks(stepLink);
    }

    private setTertiaryLinks(stepLink: StepLink) {
        const tertiaryLinks: PageHeaderNavItem[] = [];

        const isReadOnly =
            this.applicationId &&
            this.applicationFormControlManagerService.isReadOnly();
        const isFrnFilingRoute =
            RouteConstants.FrnFiling.isExistingFrnFilingRoute(stepLink.url);
        const cameFromDirectEntry =
            isFrnFilingRoute && this.applicationState?.hasFrnRequest == true;
        const shouldShowReturn = isReadOnly && cameFromDirectEntry;
        const shouldShowSaveClose =
            this.applicationState?.handoverToEwa != null &&
            this.stepNavigationOptionsService.needsSaveAndCloseButton(
                stepLink,
                this.applicationState.handoverToEwa,
            );

        if (!shouldShowReturn) {
            tertiaryLinks.push(
                this.stepNavigationButtonService.createTertiaryLink(
                    NavigationText.CANCEL,
                    this.cancel.bind(this),
                ),
            );
        }

        if (shouldShowReturn || shouldShowSaveClose) {
            let buttonText: string;

            if (shouldShowReturn) {
                buttonText = NavigationText.RETURN;
            } else if (cameFromDirectEntry) {
                buttonText = NavigationText.SAVE_AND_RETURN;
            } else {
                buttonText = NavigationText.SAVE_AND_CLOSE;
            }

            tertiaryLinks.push(
                this.stepNavigationButtonService.createTertiaryLink(
                    buttonText,
                    this.saveAndClose.bind(this),
                ),
            );
        }

        this.navItems.tertiaries = tertiaryLinks;
    }

    private setSecondaryLinks(stepLink: StepLink) {
        const secondaryLinks: PageHeaderNavItem[] = [];

        const isFrnFilingRoute =
            RouteConstants.FrnFiling.isExistingFrnFilingRoute(stepLink.url);
        const cameFromDirectEntry =
            isFrnFilingRoute && this.applicationState?.hasFrnRequest;

        if (
            !cameFromDirectEntry &&
            this.applicationState &&
            this.applicationState.handoverToEwa != null &&
            this.stepNavigationOptionsService.needsHandoverToEwaButton(
                stepLink,
                this.applicationStateService.IsApplicationTemplate(),
                this.applicationState.handoverToEwa,
            )
        ) {
            secondaryLinks.push(
                this.stepNavigationButtonService.createSecondaryLink(
                    NavigationText.HANDOVER_TO_EWA,
                    this.handoverToEwa.bind(this),
                ),
            );
        }

        this.navItems.secondaries = secondaryLinks;
    }

    private setPrimaryLinks(stepLink: StepLink) {
        const primaryLinks: PageHeaderNavItem[] = [];

        if (this.stepNavigationOptionsService.needsPreviousButton(stepLink)) {
            primaryLinks.push(
                this.stepNavigationButtonService.createPrimaryLink(
                    NavigationText.PREVIOUS,
                    this.previous.bind(this),
                ),
            );
        }

        if (this.stepNavigationOptionsService.needsNextButton(stepLink)) {
            primaryLinks.push(
                this.stepNavigationButtonService.createPrimaryLink(
                    NavigationText.NEXT,
                    this.next.bind(this),
                ),
            );
        }

        this.navItems.primaries = primaryLinks;
    }
}
